<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed z-20 inset-0 overflow-y-auto"
            @close="closeModal"
            @keydown.enter="updateShippingRegion(inputs)"
            :open="open"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    />
                </TransitionChild>

                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                    >
                        <div
                            class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                        >
                            <button
                                type="button"
                                class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                @click="closeModal"
                            >
                                <span class="sr-only">Close</span>
                                <XIcon class="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <div class="sm:flex sm:items-start">
                            <div
                                class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                            >
                                <TruckIcon
                                    class="h-6 w-6 text-nutreance-green"
                                    aria-hidden="true"
                                />
                            </div>

                            <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                <DialogTitle
                                    as="h3"
                                    class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                >
                                    Estimate Shipping
                                </DialogTitle>
                            </div>
                        </div>

                        <div
                            class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                        >
                            <div class="sm:col-span-2 sm:px-16 pb-6">
                                <div class="py-2">
                                    <label
                                        for="country_code"
                                        class="block text-sm font-medium text-gray-700"
                                    >
                                        Country
                                    </label>

                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <select
                                            v-model="inputs.country_code"
                                            :ref="refs.country_code"
                                            @change="updateCountry"
                                            id="country_code"
                                            name="country_code"
                                            autocomplete="country"
                                            required
                                            :class="[
                                                errors.country_code
                                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                            ]"
                                            :aria-invalid="
                                                errors.country_code
                                                    ? 'true'
                                                    : 'false'
                                            "
                                            :aria-describedby="
                                                errors.country_code
                                                    ? 'country_code-error'
                                                    : ''
                                            "
                                        >
                                            <option
                                                v-for="(
                                                    country, code
                                                ) in countries"
                                                :key="code"
                                                :value="code"
                                            >
                                                {{ country }}
                                            </option>
                                        </select>
                                        <div
                                            v-if="errors.country_code"
                                            class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        v-if="errors.country_code"
                                        class="mt-2 text-sm text-red-600"
                                        id="country_code-error"
                                    >
                                        {{ errors.country_code }}
                                    </p>
                                </div>

                                <div class="py-2">
                                    <label
                                        for="region"
                                        class="block text-sm font-medium text-gray-700"
                                    >
                                        {{ regionName }}
                                    </label>

                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            v-model="inputs.region"
                                            :ref="refs.region"
                                            name="region"
                                            id="region"
                                            type="text"
                                            required
                                            :class="[
                                                errors.region
                                                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                    : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                            ]"
                                            :aria-invalid="
                                                errors.region ? 'true' : 'false'
                                            "
                                            :aria-describedby="
                                                errors.region
                                                    ? 'region-error'
                                                    : ''
                                            "
                                        />
                                        <div
                                            v-if="errors.region"
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        v-if="errors.region"
                                        class="mt-2 text-sm text-red-600"
                                        id="region-error"
                                    >
                                        {{ errors.region }}
                                    </p>
                                </div>
                            </div>

                            <button
                                @click="updateShippingRegion(inputs)"
                                name="save"
                                type="submit"
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                            >
                                <LoadingIcon
                                    v-if="loading"
                                    class="h-5 w-5 mr-2"
                                />
                                <CheckIcon
                                    v-else
                                    class="h-5 w-5 mr-2"
                                    aria-hidden="true"
                                />
                                Save
                            </button>

                            <button
                                name="cancel"
                                type="button"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                @click="closeModal"
                            >
                                <XIcon
                                    class="h-5 w-5 mr-2"
                                    aria-hidden="true"
                                />
                                Cancel
                            </button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import {
    computed,
    inject,
    nextTick,
    onMounted,
    reactive,
    ref,
    watch,
} from "vue";
import type { AppEmitter } from "../../emitter";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    ExclamationCircleIcon,
    TruckIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useShippingOption } from "../../functions/useShippingOption";
import { useString } from "../../functions/useString";
import LoadingIcon from "../LoadingIcon.vue";
import { updateCountry as updateCountryRequest } from "../../api/countries";

const props = defineProps<{
    countries?: any;
    errors?: any;
    initialCountry?: any;
    initialRegion?: string;
    open?: boolean;
}>();

const emit = defineEmits<{
    (e: "closeModal"): void;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { loading, updateShippingRegion } = useShippingOption();
const { ucwords } = useString();
const inputs = reactive<Record<string, any>>({
    country_code: props.initialCountry.code,
    region: props.initialRegion,
});
const refs = {
    country_code: ref<any>(null),
    region: ref<any>(null),
};
const country = ref<any>(props.initialCountry);

const regionName = computed(() => {
    if (country.value.shipping_region_field !== "postal_code") {
        return ucwords(country.value.shipping_region_field.replace(/_/g, " "));
    }

    return ucwords(country.value.postal_code);
});

onMounted(() => emitter.on("shippingRegionUpdated", () => closeModal()));

watch(
    () => props.open,
    (open) => {
        if (open) {
            nextTick(() => refs.region.value.focus());
        }
    },
);

// FIXME(4f): `@change="updateCountry"` passes the DOM Event, but this reads
// `countryCode.length`, so on a real change event the ternary always falls
// through to `inputs.country_code`. Param typed `any` to preserve this behavior.
function updateCountry(countryCode: any): void {
    updateCountryRequest({
        country_code:
            countryCode.length === 2 ? countryCode : inputs.country_code,
    }).then((data) => {
        inputs.country_code = data.country.code;
        country.value = data.country;
    });
}

function closeModal(): void {
    emit("closeModal");
}
</script>
